import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import ProductTable from './ProductTable';
import SearchBar from './SearchBar';
import AdminHeader from './AdminHeader';
import './AdminDashboard.css';

const RetiredProductsManagement = ({ onBack }) => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [hasChanges, setHasChanges] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tableLocked, setTableLocked] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [clearEditingState, setClearEditingState] = useState(false);

  // Field name mapping to Portuguese headers
  const fieldLabels = {
    productId: 'ID',
    name: 'Nome',
    material: 'Material',
    type: 'Tipo',
    thickness: 'Espessura',
    length: 'Comprimento',
    width: 'Largura',
    price: 'Preço',
    availability: 'Disponibilidade',
    actions: 'Ações'
  };

  // Load products from cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem('aerotubos2025_products_cache');
      if (cached) {
        const data = JSON.parse(cached);
        // Ensure all products have isHidden field set to false if missing
        const normalizedProducts = data.map(product => ({
          ...product,
          isHidden: product.isHidden === true
        }));
        // Keep ALL products (including hidden ones) for retired products management
        setProducts(normalizedProducts);
        setOriginalProducts(JSON.parse(JSON.stringify(normalizedProducts)));
        setEditedData({});
        setHasChanges(false);
        return true;
      }
    } catch (err) {
      console.error('Error loading from cache:', err);
    }
    return false;
  }, []);

  // Fetch all products (including hidden ones) from server
  const fetchAllProducts = useCallback(async (forceRefresh = false) => {
    if (!token) return;

    // Try cache first if not forcing refresh
    if (!forceRefresh && loadFromCache()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl('/api/products'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure all products have isHidden field set to false if missing
        const normalizedProducts = data.map(product => ({
          ...product,
          isHidden: product.isHidden === true
        }));
        // Keep ALL products (including hidden ones) for retired products management
        setProducts(normalizedProducts);
        setOriginalProducts(JSON.parse(JSON.stringify(normalizedProducts)));
        setEditedData({});
        setHasChanges(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [token, loadFromCache]);

  // Load products on mount
  useEffect(() => {
    fetchAllProducts();
  }, [fetchAllProducts]);

  // Filter to show retired (hidden) products OR products with pending hide changes
  const retiredProducts = useMemo(() => {
    return products.filter(product => {
      // Show if currently hidden
      if (product.isHidden === true) return true;
      
      // Show if there's a pending change to hide this product
      const productEdits = editedData[product.productId];
      if (productEdits && productEdits.isHidden === true) return true;
      
      return false;
    });
  }, [products, editedData]);

  // Search and filter products
  const filteredProducts = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') return retiredProducts;

    const term = searchTerm.toLowerCase().trim();
    return retiredProducts.filter(product => 
      Object.values(product).some(value => 
        String(value).toLowerCase().includes(term)
      )
    );
  }, [retiredProducts, searchTerm]);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (!sortField) return filteredProducts;

    return [...filteredProducts].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle different data types
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sortField, sortDirection]);

  // Create display products with effective values (including pending edits)
  const displayProducts = useMemo(() => {
    return sortedProducts.map(product => {
      const productEdits = editedData[product.productId];
      if (productEdits) {
        return { ...product, ...productEdits };
      }
      return product;
    });
  }, [sortedProducts, editedData]);

  // Update product field
  const updateProduct = useCallback((productId, field, value) => {
    if (tableLocked) return;
    
    // Update editedData to track changes (don't update products array immediately)
    setEditedData(prev => {
      const newEditedData = {
        ...prev,
        [productId]: {
          ...prev[productId],
          [field]: value
        }
      };
      
      // Check if this product now matches its original state
      const originalProduct = originalProducts.find(p => p.productId === productId);
      if (originalProduct) {
        const currentEdits = newEditedData[productId] || {};
        const mergedProduct = { ...originalProduct, ...currentEdits, [field]: value };
        
        // Check if all fields match the original
        const isMatchingOriginal = Object.keys(originalProduct).every(key => {
          const originalValue = originalProduct[key];
          const currentValue = mergedProduct[key];
          
          if (key === 'isHidden') {
            const originalHidden = originalValue === true;
            const currentHidden = currentValue === true;
            return originalHidden === currentHidden;
          }
          
          return originalValue === currentValue;
        });
        
        if (isMatchingOriginal) {
          const { [productId]: removed, ...rest } = newEditedData;
          return rest;
        }
      }
      
      return newEditedData;
    });
  }, [tableLocked, originalProducts]);

  // Revert a single product field without tracking as a change
  const revertProduct = useCallback((productId, field, originalValue) => {
    if (tableLocked) return;
    
    setProducts(prevProducts => {
      return prevProducts.map(product => {
        if (product.productId === productId) {
          return { ...product, [field]: originalValue };
        }
        return product;
      });
    });
  }, [tableLocked]);

  // Check for changes based on editedData
  useEffect(() => {
    const hasChanges = Object.keys(editedData).length > 0;
    setHasChanges(hasChanges);
  }, [editedData]);

  // Save changes to server
  const saveChanges = useCallback(async () => {
    if (!token || !hasChanges || Object.keys(editedData).length === 0) return;

    setTableLocked(true);
    setUpdating(true);
    setError(null);

    try {
      // Send only the edited products to the server
      const editedProducts = Object.keys(editedData).map(id => {
        const product = products.find(p => p.productId === id);
        return { ...product, ...editedData[id] };
      });

      const response = await fetch(getApiUrl('/api/products'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: editedProducts })
      });

      if (response.ok || response.status === 201) {
        const data = await response.json();
        
        // Clear editing state first
        setEditedData({});
        setHasChanges(false);
        setClearEditingState(prev => !prev);
        
        // Refresh all products from server to get the latest state
        const allProductsResponse = await fetch(getApiUrl('/api/products'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (allProductsResponse.ok) {
          const allProductsData = await allProductsResponse.json();
          const normalizedAllProducts = allProductsData.map(product => ({
            ...product,
            isHidden: product.isHidden === true
          }));
          
          // Update both products and originalProducts with fresh data from server
          setProducts(normalizedAllProducts);
          setOriginalProducts(JSON.parse(JSON.stringify(normalizedAllProducts)));
          
          // Update cache with fresh data
          localStorage.setItem('aerotubos2025_products_cache', JSON.stringify(normalizedAllProducts));
        } else {
          // If refresh fails, at least update with the current state
          setOriginalProducts(JSON.parse(JSON.stringify(products)));
        }
        
        return { success: true, message: data.message || 'Changes saved successfully' };
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save changes');
        return { success: false, error: errorData.error };
      }
    } catch (err) {
      const errorMessage = 'Network error. Please check your connection.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setUpdating(false);
      setTableLocked(false);
    }
  }, [token, hasChanges, editedData, products]);

  // Undo changes
  const undoChanges = useCallback(() => {
    if (tableLocked) return;
    
    // Refresh from server to get the latest state
    fetchAllProducts(true).then(() => {
      setEditedData({});
      setHasChanges(false);
    });
  }, [tableLocked, fetchAllProducts]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Get sort indicator for a field
  const getSortIndicator = useCallback((field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '▲' : '▼';
  }, [sortField, sortDirection]);

  // Check if product is low stock
  const isLowStock = useCallback((stock) => {
    return stock < 20;
  }, []);

  // Format price as currency
  const formatPrice = useCallback((price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }, []);


  // Refresh from server
  const refreshFromServer = useCallback(() => {
    fetchAllProducts(true);
  }, [fetchAllProducts]);

  return (
    <div className="admin-dashboard">
      <AdminHeader 
        title="Gerenciar Produtos Aposentados" 
        onBack={onBack}
        showBackButton={true}
      />

      <main className="dashboard-main">
        <div className="breadcrumb">
          <span className="breadcrumb-item" onClick={onBack}>Painel Administrativo</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item active">Gerenciar Produtos Aposentados</span>
        </div>

        <div className="dashboard-content">
          <SearchBar 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            hasChanges={hasChanges}
            saveChanges={saveChanges}
            undoChanges={undoChanges}
            updating={updating}
            tableLocked={tableLocked}
            editedData={editedData}
            error={error}
            products={displayProducts}
            refreshFromServer={refreshFromServer}
            loading={loading}
          />
          
          {loading ? (
            <div className="loading-overlay">
              <div className="spinner"></div>
              <p>Carregando produtos aposentados...</p>
            </div>
          ) : (
            <ProductTable
              products={displayProducts}
              updateProduct={updateProduct}
              revertProduct={revertProduct}
              handleSort={handleSort}
              getSortIndicator={getSortIndicator}
              isLowStock={isLowStock}
              formatPrice={formatPrice}
              updating={updating}
              tableLocked={tableLocked}
              clearEditingState={clearEditingState}
              fieldLabels={fieldLabels}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default RetiredProductsManagement;
